import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

function PwField({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="text-sm font-medium">{label}</label>
      <input
        type="password"
        value={value}
        data-testid={testid}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-lg bg-secondary border border-input px-3 py-2 text-sm outline-none focus:border-primary transition-colors"
      />
    </div>
  );
}

export function ChangePasswordDialog({ trigger }) {
  const [open, setOpen] = useState(false);
  const [oldp, setOldp] = useState("");
  const [newp, setNewp] = useState("");
  const [conf, setConf] = useState("");

  const save = async () => {
    if (newp.length < 6) return toast.error("New password must be at least 6 characters");
    if (newp !== conf) return toast.error("Passwords do not match");
    try {
      await api.post("/auth/change-password", { old_password: oldp, new_password: newp });
      toast.success("Password changed");
      setOpen(false);
      setOldp(""); setNewp(""); setConf("");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  return (
    <>
      <span onClick={() => setOpen(true)}>{trigger}</span>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="card-surface">
          <DialogHeader><DialogTitle>Change Password</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <PwField label="Current Password" value={oldp} onChange={setOldp} testid="cp-old-input" />
            <PwField label="New Password" value={newp} onChange={setNewp} testid="cp-new-input" />
            <PwField label="Confirm New Password" value={conf} onChange={setConf} testid="cp-confirm-input" />
          </div>
          <DialogFooter>
            <button data-testid="cp-save-button" onClick={save} className="rounded-lg bg-primary text-primary-foreground px-4 py-2 text-sm font-semibold hover:bg-blue-500 transition-colors">Update Password</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function ResetPasswordDialog({ userId, userName, trigger }) {
  const [open, setOpen] = useState(false);
  const [newp, setNewp] = useState("");
  const [conf, setConf] = useState("");

  const save = async () => {
    if (newp.length < 6) return toast.error("New password must be at least 6 characters");
    if (newp !== conf) return toast.error("Passwords do not match");
    try {
      await api.put(`/users/${userId}/password`, { new_password: newp });
      toast.success(`Password reset for ${userName}`);
      setOpen(false);
      setNewp(""); setConf("");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  return (
    <>
      <span onClick={(e) => { e.stopPropagation(); setOpen(true); }}>{trigger}</span>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="card-surface" onClick={(e) => e.stopPropagation()}>
          <DialogHeader><DialogTitle>Reset Password — {userName}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">Set a new password. The existing password is never shown.</p>
            <PwField label="New Password" value={newp} onChange={setNewp} testid="rp-new-input" />
            <PwField label="Confirm New Password" value={conf} onChange={setConf} testid="rp-confirm-input" />
          </div>
          <DialogFooter>
            <button data-testid="rp-save-button" onClick={save} className="rounded-lg bg-primary text-primary-foreground px-4 py-2 text-sm font-semibold hover:bg-blue-500 transition-colors">Reset Password</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
